export type SetScoreInput = {
  setNumber: number;
  scoreSideA: number;
  scoreSideB: number;
};

export type BadmintonScoringType = 'rally_21' | 'rally_15' | 'rally_11';

export function scoringTarget(scoringType: string): number {
  if (scoringType === 'rally_15') return 15;
  if (scoringType === 'rally_11') return 11;
  return 21;
}

export function scoringCap(scoringType: string): number {
  if (scoringType === 'rally_15') return 20;
  if (scoringType === 'rally_11') return 15;
  return 30;
}

export function setsNeededToWin(maxSets: number): number {
  if (maxSets <= 1) return 1;
  if (maxSets >= 5) return 3;
  return 2;
}

export function isSetComplete(
  scoreA: number,
  scoreB: number,
  scoringType: string = 'rally_21'
): boolean {
  const target = scoringTarget(scoringType);
  const cap = scoringCap(scoringType);
  if (scoreA === cap && scoreB === cap) return false;
  if (scoreA >= cap || scoreB >= cap) {
    return scoreA !== scoreB;
  }
  if (scoreA < target && scoreB < target) return false;
  return Math.abs(scoreA - scoreB) >= 2 && (scoreA >= target || scoreB >= target);
}

export function setWinner(
  scoreA: number,
  scoreB: number,
  scoringType: string = 'rally_21'
): 'a' | 'b' | null {
  if (!isSetComplete(scoreA, scoreB, scoringType)) return null;
  return scoreA > scoreB ? 'a' : 'b';
}

export function countSetWins(
  setScores: { scoreSideA: number; scoreSideB: number }[],
  scoringType: string = 'rally_21'
): { winsA: number; winsB: number } {
  let winsA = 0;
  let winsB = 0;
  for (const s of setScores) {
    const w = setWinner(s.scoreSideA, s.scoreSideB, scoringType);
    if (w === 'a') winsA++;
    if (w === 'b') winsB++;
  }
  return { winsA, winsB };
}

export function canFillSet(
  setNumber: number,
  sets: SetScoreInput[],
  maxSets: number,
  scoringType: string = 'rally_21'
): boolean {
  if (setNumber < 1 || setNumber > maxSets) return false;
  const needed = setsNeededToWin(maxSets);
  for (let n = 1; n < setNumber; n++) {
    const prev = sets.find((s) => s.setNumber === n);
    if (!prev || !isSetComplete(prev.scoreSideA, prev.scoreSideB, scoringType)) {
      return false;
    }
  }
  const previous = sets.filter((s) => s.setNumber < setNumber);
  const { winsA, winsB } = countSetWins(previous, scoringType);
  return winsA < needed && winsB < needed;
}

export function isSet3Allowed(
  maxSets: number,
  sets: SetScoreInput[],
  scoringType: string = 'rally_21'
): boolean {
  return canFillSet(3, sets, maxSets, scoringType);
}

export function pointPickerValues(scoringType: string): number[] {
  const target = scoringTarget(scoringType);
  const start = Math.max(0, target - 11);
  const values: number[] = [];
  for (let n = start; n <= target; n++) values.push(n);
  return values;
}

export function clampScoreValue(value: number, cap: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(cap, Math.max(0, Math.trunc(value)));
}

export function applySideDelta(
  scoreSideA: number,
  scoreSideB: number,
  side: 'a' | 'b',
  delta: number,
  cap: number
): { scoreSideA: number; scoreSideB: number } {
  return applyScoreValue(
    scoreSideA,
    scoreSideB,
    side,
    (side === 'a' ? scoreSideA : scoreSideB) + delta,
    cap
  );
}

export function applyScoreValue(
  scoreSideA: number,
  scoreSideB: number,
  side: 'a' | 'b',
  value: number,
  cap: number
): { scoreSideA: number; scoreSideB: number } {
  const clamped = clampScoreValue(value, cap);
  const nextA = side === 'a' ? clamped : scoreSideA;
  const nextB = side === 'b' ? clamped : scoreSideB;
  if (nextA === cap && nextB === cap) {
    return { scoreSideA, scoreSideB };
  }
  return { scoreSideA: nextA, scoreSideB: nextB };
}

export function buildScorePayload(
  sets: SetScoreInput[],
  scoringType: string = 'rally_21'
): { setNumber: number; scoreSideA: number; scoreSideB: number }[] {
  const sorted = [...sets].sort((a, b) => a.setNumber - b.setNumber);
  const needed = setsNeededToWin(sorted.length);
  let previousComplete = true;
  let winsA = 0;
  let winsB = 0;
  let decided = false;

  return sorted.map((s) => {
    const blocked = !previousComplete || decided;
    const scoreSideA = blocked ? 0 : s.scoreSideA;
    const scoreSideB = blocked ? 0 : s.scoreSideB;
    const complete = isSetComplete(scoreSideA, scoreSideB, scoringType);
    if (complete) {
      const winner = setWinner(scoreSideA, scoreSideB, scoringType);
      if (winner === 'a') winsA += 1;
      if (winner === 'b') winsB += 1;
      if (winsA >= needed || winsB >= needed) decided = true;
    }
    previousComplete = complete;
    return { setNumber: s.setNumber, scoreSideA, scoreSideB };
  });
}

export function buildSetsFromGame(
  maxSets: number,
  setScores: { setNumber: number; scoreSideA: number; scoreSideB: number }[]
): SetScoreInput[] {
  const rows: SetScoreInput[] = [];
  for (let n = 1; n <= maxSets; n++) {
    const found = setScores.find((s) => s.setNumber === n);
    rows.push({
      setNumber: n,
      scoreSideA: found?.scoreSideA ?? 0,
      scoreSideB: found?.scoreSideB ?? 0,
    });
  }
  return rows;
}
